import { type FC, useState, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import dibsCss from 'dibs-css';
import { triggerConstants } from 'dibs-tracking';
import { SellerBrandingTrackingText } from 'dibs-buyer-layout/exports/SellerBrandingTrackingText.tsx';

// components
import { Link } from 'dibs-elements/exports/Link.tsx';
import { FormattedMessage } from 'dibs-react-intl';
import { learnMore } from '../messages/pdpBioMessages.jsx';
import { SharedStarRating } from '../../shared/SharedStarRating/SharedStarRating.tsx';
import { PdpSharedNewSellerPill } from '../shared/PdpSharedNewSellerPill/PdpSharedNewSellerPill.tsx';
import { PdpSharedSellerDetailsFeedback } from '../shared/PdpSharedSellerDetails/PdpSharedSellerDetailsFeedback.tsx';
import { PdpSharedDealerDetailsBioModalLazy } from '../PdpSharedDealerDetailsBio/PdpSharedDealerDetailsBioModalLazy.tsx';

// helpers
import { getIsNewSeller } from '../../utils/sellerHelpers.ts';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet.ts';
import {
    goldSeller,
    platinumSeller,
    diamondSeller,
    vettedSeller,
    defaultSeller,
} from '../messages/pdpDistinctionsMessages.jsx';

// types
import { type PdpSharedDealerDetailsHeader_seller$key as Seller } from './__generated__/PdpSharedDealerDetailsHeader_seller.graphql.ts';
import { type PdpSharedDealerDetailsHeader_user$key as User } from './__generated__/PdpSharedDealerDetailsHeader_user.graphql.ts';
import { type PdpSharedDealerDetailsHeader_viewer$key } from './__generated__/PdpSharedDealerDetailsHeader_viewer.graphql.ts';

// styles
import styles from './PdpSharedDealerDetailsHeader.scss';

const viewerFragment = graphql`
    fragment PdpSharedDealerDetailsHeader_viewer on Viewer {
        ...PdpSharedNewSellerPill_viewer
    }
`;

const userFragment = graphql`
    fragment PdpSharedDealerDetailsHeader_user on User {
        canShowSellerName
    }
`;

const sellerFragment = graphql`
    fragment PdpSharedDealerDetailsHeader_seller on Seller {
        ...SharedStarRating_seller
        ...PdpSharedSellerDetailsFeedback_seller
        ...PdpSharedDealerDetailsBioModalLazy_seller
        isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
        monthsSinceStartDate: timeSinceStartDate(unit: MONTH)
        sellerProfile {
            company
        }
        shippingAddress {
            displayFullCityStateCountry: displayCityStateCountry(fullCountryName: true)
        }
        sellerTiers {
            isGoldTier
            isPlatinumTier
            sellerTierDataNotFound
        }
        liveStorefrontProfile {
            headshots: photos(photoType: DEALER_STOREFRONT_HEADSHOT) {
                status
                path
            }
            galleryAlbum {
                mediaList {
                    element {
                        ... on Photo {
                            path
                        }
                    }
                }
            }
        }
        dealerDetailsHeaderFeedback: orderFeedbackPdp(
            first: 3
            minimumReviewRating: 4
            feedbackSellerPublicNoteHasText: true
        ) {
            edges {
                __typename
            }
        }
    }
`;

type Props = {
    viewer: PdpSharedDealerDetailsHeader_viewer$key | null;
    user: User | null | undefined;
    seller: Seller;
    afterDisplay: boolean;
    isMobile?: boolean;
    inlineHeader?: boolean;
    inlineBioLink?: boolean;
    isCompact?: boolean;
    hideTitle?: boolean;
    showSellerBranding?: boolean;
};

export const PdpSharedDealerDetailsHeader: FC<Props> = ({
    isMobile,
    afterDisplay,
    viewer: viewerRef,
    user: userRef,
    seller: sellerRef,
    inlineHeader = true,
    inlineBioLink = false,
    isCompact = false,
    showSellerBranding,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const seller = useFragment(sellerFragment, sellerRef);
    const {
        isRewardsTier2,
        sellerTiers,
        shippingAddress,
        monthsSinceStartDate,
        liveStorefrontProfile,
    } = seller || {};
    const canShowSellerName = afterDisplay && user?.canShowSellerName;
    const isNewSeller = getIsNewSeller(monthsSinceStartDate);
    const dealerLocation = shippingAddress?.displayFullCityStateCountry;
    const { headshots, galleryAlbum } = liveStorefrontProfile || {};
    const { status: headshotStatus, path: headshotPath } = headshots?.[0] || {};
    const showHeadshot = isRewardsTier2 && headshotStatus === 'ACTIVE' && !!headshotPath;
    const mediaList = galleryAlbum?.mediaList || [];
    const reviewHighlights = seller?.dealerDetailsHeaderFeedback?.edges;
    const reviewStarsBelow =
        isCompact &&
        isMobile &&
        reviewHighlights &&
        reviewHighlights?.length > 0 &&
        !showSellerBranding;

    const { sellerTierDataNotFound, isGoldTier, isPlatinumTier } = sellerTiers || {};
    const sellerTierDataFound = !sellerTierDataNotFound;
    const isGoldSeller = sellerTierDataFound && isGoldTier;
    const isPlatinumSeller = sellerTierDataFound && isPlatinumTier;

    const [isBioModalOpen, setIsBioModalOpen] = useState(false);

    let sellerImagePath;

    if (showHeadshot) {
        sellerImagePath = headshotPath;
    } else if (mediaList.length) {
        sellerImagePath = mediaList.find(media => media?.element?.path)?.element?.path;
    }

    const isCompactMobile = isCompact && isMobile;

    let compactSellerTitle = defaultSeller;
    if (isCompactMobile) {
        if (isGoldSeller) {
            compactSellerTitle = goldSeller;
        } else if (isPlatinumSeller) {
            compactSellerTitle = platinumSeller;
        } else if (isRewardsTier2) {
            compactSellerTitle = diamondSeller;
        } else {
            compactSellerTitle = vettedSeller;
        }
    }

    const detailsHeader = (
        <div
            data-tn="pdp-shared-dealer-details-header"
            className={classnames(styles.headerPrimary, dibsCss.pbXsmall, {
                [styles.small]: isCompact,
                [styles.noPadding]: reviewStarsBelow,
            })}
        >
            {!showSellerBranding && (
                <span
                    className={classnames(dibsCss.inline, {
                        [dibsCss.mrXsmall]: isNewSeller,
                    })}
                >
                    {canShowSellerName ? (
                        <SellerBrandingTrackingText
                            trackingTrigger={triggerConstants.TRIGGER_SELLER_INFORMATION}
                        >
                            {seller?.sellerProfile?.company}
                        </SellerBrandingTrackingText>
                    ) : (
                        <FormattedMessage
                            id="PdpSharedDealerDetailsHeader.seller_title"
                            defaultMessage="{isCompactMobile, select,
                                true {{compactSellerTitle}}
                                other {About the Seller}
                            }"
                            values={{
                                isCompactMobile,
                                compactSellerTitle,
                            }}
                        />
                    )}
                </span>
            )}
            {isNewSeller && (
                <span
                    className={classnames(styles.pill, dibsCss.inlineFlex, dibsCss.mlXxsmall, {
                        [styles.align]: !isCompact,
                    })}
                >
                    <PdpSharedNewSellerPill
                        viewer={viewer || null}
                        backgroundColor={isMobile ? 'buyer-tertiary' : 'white'}
                    />
                </span>
            )}
        </div>
    );

    // TOF-18: Image stretched vertically - https://1stdibs.atlassian.net/browse/TOF-18
    if (sellerImagePath && sellerImagePath?.includes('height')) {
        const imageUrl = new URL(sellerImagePath);
        const urlParams = new URLSearchParams(imageUrl.search);
        urlParams.delete('height');
        sellerImagePath = `${imageUrl.origin}${imageUrl.pathname}?${urlParams.toString()}`;
    }

    const sellerImage = sellerImagePath ? (
        <img
            alt="" // decorative image
            sizes="(max-width:768px) 60px, 80px, 100px"
            src={sellerImagePath}
            className={classnames(dibsCss.wFull, dibsCss.hAuto, dibsCss.minHFull)}
            srcSet={getSrcsetString(sellerImagePath, [100, 200, 300])}
        />
    ) : null;

    const reviewTotalContainer = (
        <div
            className={
                isCompact
                    ? dibsCss.flex
                    : classnames(styles.reviews, dibsCss.flex, dibsCss.pbXsmall)
            }
        >
            <div className={dibsCss.mrXsmall}>
                <SharedStarRating size={isCompact ? 'medium' : 'small'} seller={seller} />
            </div>
            <PdpSharedSellerDetailsFeedback
                seller={seller}
                showSellerBranding={showSellerBranding}
            />
        </div>
    );

    const openBioModal = useCallback(() => setIsBioModalOpen(true), []);
    const closeBioModal = useCallback(() => setIsBioModalOpen(false), []);

    return (
        <>
            {!inlineHeader && <div className={styles.inlineHeader}>{detailsHeader}</div>}
            <div
                className={classnames(styles.container, dibsCss.flex, {
                    [styles.reviewStarsBelow]: reviewStarsBelow,
                })}
            >
                {sellerImage &&
                    !showSellerBranding &&
                    ((!isRewardsTier2 && !inlineBioLink) || isCompact ? (
                        <Link
                            onClick={openBioModal}
                            className={classnames(styles.imageContainer, {
                                [styles.compact]: isCompact,
                            })}
                        >
                            {sellerImage}
                        </Link>
                    ) : (
                        <div
                            className={classnames(styles.imageContainer, {
                                [styles.compact]: isCompact,
                            })}
                        >
                            {sellerImage}
                        </div>
                    ))}
                <div className={classnames(dibsCss.flex, dibsCss.flexCol)}>
                    {inlineHeader && !showSellerBranding && detailsHeader}
                    {!reviewStarsBelow && reviewTotalContainer}
                    <span className={styles.location} data-tn="dealer-location">
                        <FormattedMessage
                            id="PdpSharedDealerDetailsHeader.location"
                            defaultMessage="Located in {dealerLocation}"
                            values={{ dealerLocation }}
                        />
                    </span>
                    {afterDisplay && (!isRewardsTier2 || isCompact) && (
                        <>
                            {inlineBioLink && (
                                <Link className={styles.learnMore} onClick={openBioModal}>
                                    {learnMore}
                                </Link>
                            )}
                            <PdpSharedDealerDetailsBioModalLazy
                                seller={seller}
                                isOpen={isBioModalOpen}
                                onClose={closeBioModal}
                                showSellerBranding={showSellerBranding}
                            />
                        </>
                    )}
                </div>
            </div>
            {reviewStarsBelow && reviewTotalContainer}
        </>
    );
};
